import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Page, Layout, Card, IndexTable, Badge, Text, Banner, Link, TextField, Select, Pagination, SkeletonBodyText, Button, Spinner, BlockStack } from '@shopify/polaris'
import { RefreshIcon } from '@shopify/polaris-icons'
import { useDraftOrders, type DraftOrderRow } from '../hooks/useDraftOrders'
import { getStatusConfig } from '../lib/status'
import { formatCurrency } from '../lib/currency'
import { adminUrl } from '../lib/admin-url'
import { apiGet } from '../lib/api'

function BnplxBadge({ payment, shopifyStatus }: { payment: DraftOrderRow['payment']; shopifyStatus: string }) {
    if (payment) {
        const config = getStatusConfig(payment.status)
        return <Badge tone={config.tone as any}>{config.label}</Badge>
    }
    if (shopifyStatus === 'OPEN') {
        return <Badge tone="attention">Guarantee Pending</Badge>
    }
    return null
}

function ShopifyStatusBadge({ status }: { status: string }) {
    switch (status) {
        case 'OPEN':
            return <Badge tone="attention">Open</Badge>
        case 'COMPLETED':
            return <Badge>Completed</Badge>
        case 'INVOICE_SENT':
            return <Badge tone="info">Invoice Sent</Badge>
        default:
            return <Badge>{status}</Badge>
    }
}

function customerName(row: DraftOrderRow): string {
    if (row.purchasingEntity?.company?.name) return row.purchasingEntity.company.name
    if (row.customer) {
        return [row.customer.firstName, row.customer.lastName].filter(Boolean).join(' ') || '—'
    }
    return '—'
}

function formatMoney(amount: string, currency: string): string {
    return formatCurrency(amount, currency)
}

function OrderLink({ payment }: { payment: DraftOrderRow['payment'] }) {
    if (!payment?.orderId) return <Text as="span" tone="subdued">—</Text>
    return (
        <span onClick={(e) => e.stopPropagation()}>
            <Link
                url={adminUrl(`orders/${payment.orderId}`)}
                target="_top"
            >
                {payment.orderName || `#${payment.orderId}`}
            </Link>
        </span>
    )
}

export default function DraftOrdersList() {
    const navigate = useNavigate()
    const [configChecked, setConfigChecked] = useState(false)
    const [configMissing, setConfigMissing] = useState(false)

    // Check if tenant is configured
    useEffect(() => {
        apiGet<{ shop: string; config: any }>('/api/settings')
            .then(data => {
                if (!data.config) setConfigMissing(true)
            })
            .catch(() => {})
            .finally(() => setConfigChecked(true))
    }, [])

    const { items, loading, error, hasNextPage, hasPreviousPage, nextPage, prevPage, refresh } = useDraftOrders()
    const [searchField, setSearchField] = useState('name')
    const [queryValue, setQueryValue] = useState('')

    const searchFieldOptions = [
        { label: 'Draft Order', value: 'name' },
        { label: 'Customer', value: 'customer' },
        { label: 'Draft Order Status', value: 'status' },
        { label: 'BNPLX Status', value: 'bnplx' },
    ]

    const filteredItems = useMemo(() => {
        return items.filter(row => {
            if (!queryValue) return true
            const q = queryValue.toLowerCase()
            switch (searchField) {
                case 'name':
                    return row.name.toLowerCase().includes(q)
                case 'customer':
                    return customerName(row).toLowerCase().includes(q)
                case 'status':
                    return row.status.toLowerCase().includes(q)
                case 'bnplx': {
                    const bnplxStatus = row.payment?.status || 'pending'
                    const config = getStatusConfig(bnplxStatus)
                    return bnplxStatus.toLowerCase().includes(q) || config.label.toLowerCase().includes(q)
                }
                default:
                    return true
            }
        })
    }, [items, searchField, queryValue])

    const selectedFieldLabel = searchFieldOptions.find(o => o.value === searchField)?.label || 'Name'

    if (!configChecked) {
        return (
            <Page title="BNPLX B2B">
                <Card>
                    <BlockStack gap="400" inlineAlign="center">
                        <Spinner size="large" />
                        <Text as="p" tone="subdued">Loading...</Text>
                    </BlockStack>
                </Card>
            </Page>
        )
    }

    const initialLoad = loading && items.length === 0
    const resourceName = { singular: 'draft order', plural: 'draft orders' }

    const skeletonRows = Array.from({ length: 5 }, (_, i) => (
        <IndexTable.Row id={`skeleton-${i}`} key={`skeleton-${i}`} position={i}>
            {Array.from({ length: 6 }, (_, j) => (
                <IndexTable.Cell key={j}>
                    <SkeletonBodyText lines={1} />
                </IndexTable.Cell>
            ))}
        </IndexTable.Row>
    ))

    const rowMarkup = filteredItems.map((row, index) => {
        const id = row.id.split('/').pop()!
        const total = row.totalPriceSet.shopMoney
        return (
            <IndexTable.Row
                id={row.id}
                key={row.id}
                position={index}
                onClick={() => navigate(`/draft-orders/${id}`)}
            >
                <IndexTable.Cell>
                    <Text as="span" fontWeight="bold">{row.name}</Text>
                </IndexTable.Cell>
                <IndexTable.Cell>{customerName(row)}</IndexTable.Cell>
                <IndexTable.Cell>{formatMoney(total.amount, total.currencyCode)}</IndexTable.Cell>
                <IndexTable.Cell><ShopifyStatusBadge status={row.status} /></IndexTable.Cell>
                <IndexTable.Cell><BnplxBadge payment={row.payment} shopifyStatus={row.status} /></IndexTable.Cell>
                <IndexTable.Cell><OrderLink payment={row.payment} /></IndexTable.Cell>
            </IndexTable.Row>
        )
    })

    return (
        <Page
            title="BNPLX B2B"
            primaryAction={{ content: 'Create Draft Order', onAction: () => navigate('/create') }}
            secondaryActions={[
                { content: 'Settings', onAction: () => navigate('/settings') },
            ]}
            titleMetadata={
                <span style={{ display: 'inline-flex', animation: loading ? 'spin 0.8s linear infinite' : 'none' }}>
                    <Button
                        icon={RefreshIcon}
                        variant="plain"
                        onClick={refresh}
                        accessibilityLabel="Refresh"
                    />
                </span>
            }
        >
            <style>{`@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }`}</style>
            <Layout>
                <Layout.Section>
                    {configMissing && (
                        <Banner tone="warning">
                            BNPLX is not configured yet. Enter your credentials in Settings to start processing payments.
                        </Banner>
                    )}
                    {error && <Banner tone="critical" onDismiss={refresh}>{error}</Banner>}
                    <Card padding="0">
                        <div style={{ padding: '12px' }}>
                            <TextField
                                label="Search"
                                labelHidden
                                value={queryValue}
                                onChange={setQueryValue}
                                placeholder={`Search by ${selectedFieldLabel}`}
                                clearButton
                                onClearButtonClick={() => setQueryValue('')}
                                autoComplete="off"
                                connectedLeft={
                                    <Select
                                        label="Search field"
                                        labelHidden
                                        options={searchFieldOptions}
                                        value={searchField}
                                        onChange={setSearchField}
                                    />
                                }
                            />
                        </div>
                        <IndexTable
                            resourceName={resourceName}
                            itemCount={initialLoad ? 5 : filteredItems.length}
                            loading={false}
                            headings={[
                                { title: 'Draft Order' },
                                { title: 'Customer' },
                                { title: 'Total' },
                                { title: 'Draft Order Status' },
                                { title: 'BNPLX Status' },
                                { title: 'Order' },
                            ]}
                            selectable={false}
                        >
                            {initialLoad ? skeletonRows : rowMarkup}
                        </IndexTable>
                    </Card>
                    {(hasPreviousPage || hasNextPage) && (
                        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '16px' }}>
                            <Pagination
                                hasPrevious={hasPreviousPage}
                                hasNext={hasNextPage}
                                onPrevious={prevPage}
                                onNext={nextPage}
                            />
                        </div>
                    )}
                </Layout.Section>
            </Layout>
        </Page>
    )
}
